import { ApiServiceFactory } from '@/factories/api-service.factory.ts'
import type { ApiService } from '@/services/api/api.service.ts'
import { convertXML } from 'simple-xml-to-json'
import type { PayrollTax, PayrollTaxApiResult, PayrollTaxApiResultOutput } from '@/ts/types/payroll-tax.type.ts'
import { logError } from '@/helpers/logging.helpers.ts'
import { BmfApiError } from '@/ts/classes/errors/bmf-api.error.ts'
import { useGlobalStore } from '@/stores/globalStore.ts'
import type { BasicAuth } from '@/ts/types/basic-auth.type'

function deserializeBmfApiResult(bmfApiResult: PayrollTaxApiResult): PayrollTax {
  const ausgabenChild = bmfApiResult.lohnsteuer.children.find((child) => child.ausgaben !== undefined)
  const outputs = ausgabenChild?.ausgaben?.children.map((output) => (output as PayrollTaxApiResultOutput).ausgabe) ?? []

  if (outputs.length === 0) {
    const firstChild = bmfApiResult.lohnsteuer.children[0]
    const bmfErrorMessage = firstChild?.information?.content ?? 'Unprocessable content'
    throw new BmfApiError({ cause: bmfErrorMessage })
  }

  const salaryTax = outputs.find((output) => output.name === 'LSTLZZ')?.value ?? '0'
  const solidarityTax = outputs.find((output) => output.name === 'SOLZLZZ')?.value ?? '0'
  const churchTaxCalculationBasis = outputs.find((output) => output.name === 'BK')?.value ?? '0'

  return {
    salaryTax: parseInt(salaryTax),
    solidarityTax: parseInt(solidarityTax),
    churchTaxCalculationBasis: parseInt(churchTaxCalculationBasis)
  }
}

export class BmfApiService {
  private service: ApiService
  private basicAuth?: BasicAuth

  constructor(basicAuth?: BasicAuth) {
    this.service = new ApiServiceFactory().createApiService(
      () => {},
      {
        Accept: 'text/xml',
        'Content-Type': 'text/xml'
      },
      'bmf',
      import.meta.env.VITE_BMF_BASE_URL,
      60000
    )
    this.basicAuth = basicAuth
  }

  async getPayrollTax(
    annualSalaryInCents: number,
    taxClass: number,
    isLiableToChurchTax: number,
    statutoryPensionInsurance: number,
    statutoryCareInsurance: number,
    statutoryHealthInsurance: number,
    consideredChildrenAboveOneFactor: number,
    childAllowanceCount: number,
    employeeAdditionalSocialInsurance: number,
    specialStatutoryCareInsurance: number = 0,
    duration: number = 1
  ): Promise<PayrollTax> {
    const { updateApiErrorState } = useGlobalStore()
    try {
      updateApiErrorState(false)
      const { username, password } = this.basicAuth ?? {}
      const config: Record<string, any> = {
        params: {
          code: `${import.meta.env.VITE_BMF_API_CODE}`,
          LZZ: duration,
          RE4: annualSalaryInCents,
          STKL: taxClass,
          R: isLiableToChurchTax,
          KVZ: employeeAdditionalSocialInsurance,
          PKV: statutoryHealthInsurance,
          PVA: consideredChildrenAboveOneFactor,
          KRV: statutoryPensionInsurance,
          PVZ: statutoryCareInsurance,
          PVS: specialStatutoryCareInsurance,
          ZKF: childAllowanceCount
        }
      }

      if (username && password) config['auth'] = { username, password }

      const res = await this.service.get('bmf-tax-calc', config)

      const convertedRes = convertXML(res.data) as PayrollTaxApiResult
      return deserializeBmfApiResult(convertedRes)
    } catch (e) {
      logError('BmfService.getPayrollTax', e as Error)
      updateApiErrorState(true)
      throw new BmfApiError({ cause: e as Error })
    }
  }
}
